import React, { Dispatch } from 'react';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { callPaginatedAPI } from '../../Components/Utilities/callAPI';
import queryString from 'query-string';
import {
  GenericTableAction,
  GenericTableState
} from '../../Components/GenericTable/genericTableReducer';
import { headerStyles } from './emailBatchConfig';
import EmailBatchTableRows from './EmailBatchTableRows';
import GenericTable from '../../Components/GenericTable/GenericTable';
import SearchInput from '../../Components/GenericTable/SearchInput';
import { CandidateSourcingAction } from '../candidateSourcingReducer';

interface EmailBatch {
  id: number;
  email_template: string;
  user: {
    name: string;
    link: string;
  };
  recipients: number;
  delayed_to?: string;
  created_at: string;
  status: string;
}

interface EmailBatchesApiResponse {
  data: {
    email_batches: EmailBatch[];
  };
  totalItems: number;
  totalPages: number;
}

interface EmailTabProps {
  isOpen: boolean;
  selectedPool: {
    id: number;
    title: string;
  };
  emailsState: GenericTableState;
  emailsDispatch: Dispatch<GenericTableAction>;
  candidateSourcingDispatch: Dispatch<CandidateSourcingAction>;
}

const EmailTab: React.FC<EmailTabProps> = ({
  isOpen,
  selectedPool,
  emailsState,
  emailsDispatch,
  candidateSourcingDispatch
}) => {
  const { data: emails, isFetching } = useQuery({
    queryKey: [
      'candidateSourcingPoolEmails',
      selectedPool.id,
      emailsState.searchQuery,
      emailsState.searchSettings
    ],
    queryFn: () => {
      return callPaginatedAPI(
        `/api/v4/candidate_sourcing/candidate_pools/${selectedPool.id}/email_batches?${queryString.stringify(
          {
            ...emailsState.searchSettings,
            'q[search]': emailsState.searchQuery
          },
          {
            skipEmptyString: true,
            skipNull: true,
            arrayFormat: 'bracket',
            encode: false
          }
        )}`,
        null,
        'GET'
      ) as Promise<EmailBatchesApiResponse>;
    },
    onError() {
      console.error('Error fetching email batches');
    },
    enabled: isOpen,
    refetchOnMount: true,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false
  });

  // Listen for row selection from the table and dispatch analytics state
  React.useEffect(() => {
    if (emailsState.selectedRowTitle?.item) {
      const emailBatch = emailsState.selectedRowTitle.item as EmailBatch;
      candidateSourcingDispatch({
        type: 'SET_SELECTED_EMAIL_FOR_ANALYTICS',
        payload: {
          item: {
            id: emailBatch.id,
            email_template: emailBatch.email_template,
            user: emailBatch.user,
            recipients: emailBatch.recipients,
            delayed_to: emailBatch.delayed_to,
            created_at: emailBatch.created_at,
            status: emailBatch.status
          },
          poolId: selectedPool.id
        }
      });
      // Clear selectedRowTitle immediately after setting selectedEmailForAnalytics
      // This prevents the useEffect from re-triggering when going back
      emailsDispatch({ type: 'REMOVE_SELECTED_ROW_DATA' });
    }
  }, [emailsState.selectedRowTitle, candidateSourcingDispatch, selectedPool.id, emailsDispatch]);

  return (
    <Box>
      <SearchInput dispatch={emailsDispatch} id="search-box-emails-tab" />
      <GenericTable
        items={emails?.data.email_batches || []}
        isLoadingItems={isFetching}
        dispatch={emailsDispatch}
        tableState={emailsState.tableState}
        paginationState={emailsState.searchSettings}
        pagination={{
          totalItems: emails?.totalItems || 0,
          totalPages: emails?.totalPages || 0
        }}
        headerStyle={headerStyles}
        TableRows={EmailBatchTableRows}
        label="emails"
        titleCellId="email_template"
        allowMultipleSelection={false}
        customEmptyStateMessage="have been sent yet"
      />
    </Box>
  );
};

export default EmailTab;
